/**
 * @file MultiSelectSearchInputContainer — owns the clear handler and default
 * placeholder for the MultiSelect type-to-filter input. The search text itself
 * is controlled by the parent MultiSelect container so selection state and
 * search state stay decoupled.
 */
import { useCallback } from "react";

import MultiSelectSearchInputView from "./MultiSelectSearchInputView";

/**
 * Props for MultiSelectSearchInputContainer.
 */
interface MultiSelectSearchInputContainerProps {
  /** Current search text (controlled by the parent MultiSelect container). */
  readonly searchText?: string;
  /** Placeholder for the search input; defaults to "Search {title}". */
  readonly searchPlaceholder?: string;
  /** Picker title, used to build the default placeholder. */
  readonly title: string;
  /** Handler invoked as the user types. */
  readonly onSearchTextChange?: (value: string) => void;
}

/**
 * Container for the MultiSelect type-to-filter input.
 * @param root0 - Component props
 * @param root0.searchText - Current search text
 * @param root0.searchPlaceholder - Placeholder for the search input
 * @param root0.title - Picker title, used to build the default placeholder
 * @param root0.onSearchTextChange - Handler invoked as the user types
 * @returns The configured search input view
 */
const MultiSelectSearchInputContainer = ({
  searchText,
  searchPlaceholder,
  title,
  onSearchTextChange,
}: MultiSelectSearchInputContainerProps) => {
  const handleClear = useCallback(() => {
    onSearchTextChange?.("");
  }, [onSearchTextChange]);

  const handleChangeText = useCallback(
    (value: string) => {
      onSearchTextChange?.(value);
    },
    [onSearchTextChange]
  );

  const value = searchText ?? "";

  return (
    <MultiSelectSearchInputView
      value={value}
      placeholder={searchPlaceholder ?? `Search ${title}`}
      showClear={value.length > 0}
      onChangeText={handleChangeText}
      onClear={handleClear}
    />
  );
};

MultiSelectSearchInputContainer.displayName = "MultiSelectSearchInputContainer";

export default MultiSelectSearchInputContainer;
